import { useEffect, useState } from 'react';
import { useAiProvider } from '../../lib/ai-provider';
import { consumeEmailReturn, rememberEmailReturn } from '../../lib/email-authoring-draft';
import { useMutation, useQuery } from '@tanstack/react-query';
import { emailApi, emailKey } from '../../lib/email-api';
import { gmailReturnUrl, gmailConnectionError } from '../../lib/gmail-connection';
import { EmailDemo, GmailMark } from './MailSurface';
import { IntegrationBar } from '../integrations/IntegrationBar';
import integrationStyles from '../integrations/Integrations.module.css';
import styles from './GmailPreferences.module.css';

export function useGmailConnection() {
  return useQuery({ queryKey: [...emailKey(), 'connections'], queryFn: emailApi.connections,
    retry: false, retryOnMount: false, staleTime: 15_000,
    refetchInterval: q => {
      if (q.state.status === 'error' || !q.state.data) return false;
      if (q.state.data.connections.some(c => ['disconnecting', 'importing'].includes(c.status) ||
          ['queued', 'running'].includes(c.sync?.status ?? ''))) return 5000;
      return q.state.data.connections.some(c => c.status === 'active') ? 60_000 : false;
    } });
}

type GmailConnectOptions = { reconnect?: boolean; disabled?: boolean; accountOnly?: boolean; autoReply?: boolean };

export function useGmailConnect({ reconnect = false, disabled = false, accountOnly = false, autoReply = true }: GmailConnectOptions = {}) {
  const provider = useAiProvider();
  const connect = useMutation({ mutationFn: () => {
    rememberEmailReturn();
    return emailApi.start(gmailReturnUrl(), reconnect || accountOnly || !autoReply ? undefined : {
      fundingAuthorized: true,
      replyLimitMicrounits: provider.usesPersonalKey ? 0 : 20_000_000,
    });
  }, onSuccess: data => window.location.assign(data.authorizationUrl) });
  const replies = !reconnect && !accountOnly && autoReply;
  const unavailable = disabled || connect.isPending || (replies && (provider.isPending || provider.isError));
  return { provider, connect, replies, reconnect,
    demo: {
      onConnect: () => connect.mutate(), disabled: unavailable, pending: connect.isPending,
      label: connect.isPending ? 'Opening Google…' : reconnect ? 'Reconnect Gmail' : 'Connect Gmail',
      error: connect.error?.message,
    },
  };
}

export function GmailConnectAction({ connection, compact = false }: { connection: ReturnType<typeof useGmailConnect>; compact?: boolean }) {
  const { provider, connect, replies, demo } = connection;
  return <div className={styles.connectSetup} data-compact={compact}>
    {!compact && replies && <p className={styles.help}>{provider.usesPersonalKey
      ? 'Automatic replies use your API key, billed by your model provider. You can pause them in Gmail settings.'
      : <>Connecting enables automatic reply preparation with a $20 monthly Resonance credit limit. Change the limit or pause replies in <a href="/app/settings/gmail">Gmail settings</a>.</>}</p>}
    <button type="button" className={styles.primary} disabled={demo.disabled} onClick={demo.onConnect}>
      <span aria-hidden="true"><GmailMark /></span>{!compact && replies && !connect.isPending ? 'Connect Gmail & enable replies' : demo.label}
    </button>
    {replies && provider.isError && <p role="alert">Couldn’t check your AI provider. <button type="button" onClick={() => void provider.refetch()}>Try again</button></p>}
    {connect.error && <p role="alert">{connect.error.message}</p>}
  </div>;
}

export function ConnectGmailButton({ compact = false, ...options }: GmailConnectOptions & { compact?: boolean }) {
  const connection = useGmailConnect(options);
  return <GmailConnectAction connection={connection} compact={compact} />;
}

export function GmailSetupResult() {
  useEffect(() => { const path = consumeEmailReturn(); if (path) window.location.replace(`${path}${window.location.search}`); }, []);
  const query = useGmailConnection();
  const params = new URLSearchParams(window.location.search);
  const error = gmailConnectionError(window.location.search);
  if (error) return <p role="alert">{error}</p>;
  if (params.get('gmail') !== 'connected') return null;
  if (query.data?.connections.some(c => c.autoDraft && c.fundingAuthorized) && query.data.draftingEnabled) return null;
  if (params.get('gmail_replies') === 'setup_required') return <p role="status">Gmail is connected, but automatic reply setup wasn’t completed. Your saved preferences were kept. <a href="/app/settings/gmail">Finish reply setup</a>.</p>;
  // Current connection state owns the status; a return URL is not evidence that
  // replies remain enabled after the user changes preferences.
  return null;
}

export function GmailConnectionNotice({ showConnected = true }: { showConnected?: boolean }) {
  const query = useGmailConnection();
  const connection = query.data?.connections[0];
  if (query.isLoading) return <p role="status">Checking Gmail…</p>;
  if (query.isError) return <p role="alert">Couldn’t check Gmail. <button onClick={() => void query.refetch()}>Try again</button></p>;
  if (connection && ['active', 'importing'].includes(connection.status)) return <><GmailSetupResult />{showConnected && <div className={styles.accountLine}><GmailMark /><span>{connection.accountEmail}</span><a href="/app/settings/gmail">Manage</a></div>}</>;
  return <div className={styles.connectionNotice}>
    <GmailSetupResult />
    <p>{connection?.status === 'disconnecting' ? 'Disconnecting Gmail…' : connection?.status === 'paused' ? 'Gmail is paused. Resume in settings to activate a sequence.' : 'Connect Gmail when you’re ready to use your sequences.'}</p>
    {connection && ['paused', 'disconnecting'].includes(connection.status) ? <a href="/app/settings/gmail">Gmail settings</a> :
      <ConnectGmailButton reconnect={connection?.status === 'reconnect_required'} disabled={!(query.data?.canConnect ?? query.data?.enabled)} />}
    {query.data?.setupIssue && <p role="status">{query.data.setupIssue.message}</p>}
  </div>;
}

type GmailSetupCardProps = {
  surface?: 'chat' | 'sequences'; initiallyOpen?: boolean; open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined; hidden?: boolean; bare?: boolean;
};
export function useGmailSetup() {
  const [autoReply, setAutoReply] = useState(true);
  const query = useGmailConnection();
  const connect = useGmailConnect({ autoReply, reconnect: query.data?.connections[0]?.status === 'reconnect_required', disabled: !(query.data?.canConnect ?? query.data?.enabled) });
  return { autoReply, setAutoReply, query, connect };
}
export function GmailSetupCard({ setup, ...props }: GmailSetupCardProps & { setup?: ReturnType<typeof useGmailSetup> }) {
  return setup ? <GmailSetupCardContent {...props} setup={setup} /> : <StandaloneGmailSetupCard {...props} />;
}
function StandaloneGmailSetupCard(props: GmailSetupCardProps) {
  const setup = useGmailSetup();
  return <GmailSetupCardContent {...props} setup={setup} />;
}
function GmailSetupCardContent({ surface = 'chat', initiallyOpen = true, open, onOpenChange, hidden = false, bare = false, setup }: GmailSetupCardProps & { setup: ReturnType<typeof useGmailSetup> }) {
  const { autoReply, setAutoReply, query, connect } = setup;
  const connection = query.data?.connections[0];
  const connected = connection && ['active', 'importing'].includes(connection.status);
  const connectAction = <GmailConnectAction compact connection={connect} />;
  const canSetUp = !query.isLoading && !query.isError && !connected && !['paused', 'disconnecting'].includes(connection?.status ?? '');
  return <IntegrationBar provider="gmail" title="Connect Gmail" summary="" hideSummary initiallyOpen={initiallyOpen}
    open={open} onOpenChange={onOpenChange} hidden={hidden} bare={bare}
    expandedAction={canSetUp ? connectAction : undefined}>
    <GmailSetupResult />
    {query.isLoading ? <p role="status">Checking Gmail…</p>
      : query.isError ? <p role="alert">Couldn’t check Gmail. <button onClick={() => void query.refetch()}>Try again</button></p>
      : connected ? <p>{connection.accountEmail} · <a href="/app/settings/gmail">Manage Gmail</a></p>
      : connection && ['paused', 'disconnecting'].includes(connection.status)
        ? <p>Gmail is {connection.status}. <a href="/app/settings/gmail">Gmail settings</a></p>
        : <GmailIntroduction connection={connect.demo}>{connectAction}</GmailIntroduction>}
    {canSetUp && <GmailAutoReplyPreference enabled={autoReply} onChange={setAutoReply} />}

    {query.data?.setupIssue && <p role="status">{query.data.setupIssue.message}</p>}
    {query.data?.connections.some(c => c.status === 'active') && <p role="status">{surface === 'chat' ? 'Gmail is connected. Continue your sequence request in this conversation.' : 'Gmail is connected. Your sequences are ready to configure.'}</p>}
    {query.data?.connections.some(c => c.status === 'importing') && <p role="status">Gmail is connected and importing. Wait for setup to finish before activating a sequence.</p>}
  </IntegrationBar>;
}

export function GmailAutoReplyPreference({ enabled, onChange }: { enabled: boolean; onChange: (enabled: boolean) => void }) {
  const provider = useAiProvider();
  return <label className={integrationStyles.autoReply}>
    <span><strong>Enable auto reply</strong><span>Prepare replies to new emails from CRM contacts. You approve every send.</span>
      {enabled && <small>{provider.usesPersonalKey ? 'Uses your API key, billed by your provider.' : 'Uses Resonance credits, with a $20 monthly limit.'} Change or pause in Gmail settings.</small>}
    </span>
    <input type="checkbox" role="switch" aria-label="Enable auto reply" checked={enabled} onChange={event => onChange(event.target.checked)} />
  </label>;
}

export function GmailIntroduction({ children, connection }: { children: React.ReactNode; connection: ReturnType<typeof useGmailConnect>['demo'] }) {
  return <div className={integrationStyles.preview}><EmailDemo connectAction={children} connection={connection} /></div>;
}
